import { setUser } from "@sentry/browser"
import { createContext, PropsWithChildren, useContext, useEffect } from "react"
import { segmentIdentify } from "~/app/utils/segmentHelpers"
import { ErrorPage } from "~/shared/pages/system/ErrorPage"
import { useAccountAspect } from "../AccountAspectContext"
import { RoleTermsAndConditionsChecker } from "./components/RoleTermsAndConditionsChecker"
import { KYC_REFETCH_INTERVAL, KYC_STATUS_THAT_SHOULD_REFETCH } from "./constants"
import { useIdleMonitor } from "./hooks/useIdleMonitor"
import { useMeQuery } from "./queries/useMe"
import { MeContextValue } from "./types"
import { ServerError } from "@apollo/client"

const MeContext = createContext<MeContextValue | null>(null)

export const useMeContext = () => {
  const context = useContext(MeContext)

  if (!context) throw new Error("useMeContext must be called within MeProvider")

  return context
}

const MeProvider = ({ children }: PropsWithChildren<unknown>) => {
  useIdleMonitor()
  const { currentAspect } = useAccountAspect()
  const { loading, data, refetch, error } = useMeQuery()

  useEffect(() => {
    const shouldScheduleRefetch =
      data?.me?.latestKycVerification &&
      KYC_STATUS_THAT_SHOULD_REFETCH.includes(data?.me?.latestKycVerification?.status)
    const interval = shouldScheduleRefetch && setInterval(refetch, KYC_REFETCH_INTERVAL)

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [refetch, data])

  useEffect(() => {
    if (data?.me) {
      segmentIdentify({
        me: data.me,
        company_id: currentAspect?.company?.id || currentAspect?.id,
      })

      setUser(data.me)
    }
  }, [currentAspect, data])

  if ((error?.networkError as ServerError)?.statusCode === 429) return <ErrorPage errorCode="429" />
  if (error) return <ErrorPage errorCode="500" />

  return (
    <MeContext.Provider value={{ me: loading ? undefined : data?.me, refetch }}>
      {data?.me?.roles && <RoleTermsAndConditionsChecker roles={data.me.roles} />}
      {children}
    </MeContext.Provider>
  )
}

export default MeProvider
